<template>
  <div
    class="fixed inset-0 z-[100] flex items-center justify-center bg-ink/50 px-4"
  >
    <div ref="dialogPanel" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId" tabindex="-1" @keydown="onDialogKeydown" class="relative w-full max-w-sm paper-card rounded-md p-6 animate-[login-pop_0.2s_ease-out]">
      <LanguageSwitcher class="absolute top-3 left-3" />
      <button
        @click="$emit('close')"
        class="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
        :aria-label="$t('liveHome.close')"
      >
        <X class="w-4 h-4" />
      </button>

      <div class="flex flex-col items-center text-center mb-5">
        <img :src="logoMark" alt="EcoWay" class="w-12 h-12 rounded-md shadow-ink-sm mb-3 object-cover" />
        <h2 :id="dialogTitleId" class="text-[18px] font-semibold">{{ title }}</h2>
        <p class="text-[12px] text-ink2 mt-1">{{ subtitle }}</p>
      </div>

      <!-- Коллизия при регистрации: "это вы?" -->
      <CredentialConflictCard
        v-if="mode === 'conflict' && conflict"
        :profile="conflict.profile"
        :link-token="conflict.link_token"
        @resolved="onConflictResolved"
        @cancel="onCancelConflict"
      />

      <!-- Письмо отправлено (регистрация или сброс пароля) -->
      <div v-else-if="mode === 'registered' || mode === 'forgotSent'" class="flex flex-col items-center gap-3 text-center py-2">
        <CircleCheck class="w-8 h-8 text-[hsl(var(--primary))]" />
        <p class="text-[13px] text-ink2">
          {{ mode === "registered" ? $t("login.registerSuccessBody", { email: identifier }) : $t("login.forgotSuccessBody", { email: identifier }) }}
        </p>
        <button
          type="button"
          @click="mode = 'login'"
          class="text-[12px] font-semibold text-[hsl(var(--primary))] hover:underline"
        >
          {{ $t("login.backToLogin") }}
        </button>
      </div>

      <template v-else>
        <div v-if="error" class="flex items-start gap-2 px-3 py-2 rounded-md bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))] mb-3">
          <CircleAlert class="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <p class="text-[12px] leading-snug">{{ error }}</p>
        </div>

        <form @submit.prevent="onSubmit" class="space-y-3">
          <!-- Идентификатор (email или телефон) - скрыт после отправки OTP/на
               шаге пароля регистрации по телефону, вместо него статичная
               строка с номером и ссылкой "изменить". -->
          <div v-if="!identifierLocked" class="relative">
            <component
              :is="looksLikePhone ? Phone : Mail"
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink2 pointer-events-none"
            />
            <input
              v-model="identifier"
              :type="mode === 'forgot' ? 'email' : 'text'"
              required
              autocomplete="username"
              :placeholder="$t('login.email')"
              :aria-label="$t('login.email')"
              class="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
            />
          </div>
          <div v-else class="flex items-center justify-between gap-2 px-3 h-10 rounded-md bg-[hsl(var(--muted)/0.4)] text-[13px]">
            <span class="flex items-center gap-2 truncate"><Phone class="w-4 h-4 text-ink2 shrink-0" />{{ identifier }}</span>
            <button type="button" @click="resetPhoneFlow" class="text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline shrink-0">
              {{ $t("login.changeNumber") }}
            </button>
          </div>

          <!-- Пароль: логин по паролю, регистрация по email, шаг создания пароля при регистрации по телефону -->
          <div v-if="showPasswordField" class="relative">
            <Lock class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink2 pointer-events-none" />
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              required
              :minlength="showPasswordConfirm ? 8 : undefined"
              :autocomplete="showPasswordConfirm ? 'new-password' : 'current-password'"
              :placeholder="$t('login.password')"
              :aria-label="$t('login.password')"
              class="w-full h-10 pl-9 pr-9 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
            />
            <button
              type="button"
              tabindex="-1"
              @click="showPassword = !showPassword"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink2 hover:text-[hsl(var(--foreground))] p-1 transition-colors"
              :aria-label="showPassword ? $t('login.hidePassword') : $t('login.showPassword')"
            >
              <MorphIcon :icon="showPassword ? MorphEyeOff : MorphEye" class="w-4 h-4" />
            </button>
          </div>

          <!-- Подтверждение пароля - только там, где пароль только что задаётся -->
          <div v-if="showPasswordConfirm" class="flex flex-col gap-1">
            <div class="relative">
              <Lock class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink2 pointer-events-none" />
              <input
                v-model="confirmPassword"
                :type="showPassword ? 'text' : 'password'"
                required
                minlength="8"
                autocomplete="new-password"
                :placeholder="$t('login.confirmPassword')"
                :aria-label="$t('login.confirmPassword')"
                class="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
              />
            </div>
            <span class="text-[11px] text-ink2">{{ $t("login.passwordHint") }}</span>
          </div>

          <!-- Email как опциональный второй способ входа при регистрации по телефону -->
          <div v-if="showOptionalEmail" class="flex flex-col gap-1">
            <div class="relative">
              <Mail class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink2 pointer-events-none" />
              <input
                v-model="phoneOptionalEmail"
                type="email"
                autocomplete="email"
                :placeholder="$t('login.optionalEmailLabel')"
                :aria-label="$t('login.optionalEmailLabel')"
                class="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
              />
            </div>
            <span class="text-[11px] text-ink2">{{ $t("login.optionalEmailHint") }}</span>
          </div>

          <!-- Код из WhatsApp: 4 ячейки, авто-переход между ними, вставка в
               любую ячейку распределяется по всем. -->
          <div v-if="showOtpField" class="flex justify-center gap-2.5" role="group" :aria-label="$t('login.otpCodeLabel')">
            <input
              v-for="(_, i) in otpDigits"
              :key="i"
              :ref="(el) => setOtpCellRef(el as HTMLInputElement | null, i)"
              v-model="otpDigits[i]"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              @input="onOtpDigitInput(i, $event)"
              @keydown="onOtpDigitKeydown(i, $event)"
              @paste="onOtpPaste(i, $event)"
              class="w-12 h-14 text-center text-[20px] font-semibold rounded-md border border-border bg-[hsl(var(--muted)/0.4)] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
            />
          </div>

          <div v-if="showLoginMethodToggle" class="flex justify-between items-center">
            <button
              type="button"
              @click="toggleLoginMethod"
              class="text-[11px] font-semibold text-ink2 hover:text-[hsl(var(--primary))] transition-colors"
            >
              {{ loginMethod === "password" ? $t("login.loginWithCode") : $t("login.loginWithPassword") }}
            </button>
            <button
              v-if="loginMethod === 'password'"
              type="button"
              @click="mode = 'forgot'"
              class="text-[11px] font-semibold text-ink2 hover:text-[hsl(var(--primary))] transition-colors"
            >
              {{ $t("login.forgotPassword") }}
            </button>
          </div>
          <button
            v-else-if="mode === 'login' && loginMethod === 'password'"
            type="button"
            @click="mode = 'forgot'"
            class="block text-[11px] font-semibold text-ink2 hover:text-[hsl(var(--primary))] transition-colors"
          >
            {{ $t("login.forgotPassword") }}
          </button>

          <div class="flex gap-2 pt-1">
            <button
              type="button"
              @click="onCancelClick"
              class="flex-1 h-10 rounded-md border border-border text-[13px] font-semibold hover:bg-[hsl(var(--muted))] transition-colors"
            >{{ mode === "login" ? $t('login.cancel') : $t("login.backToLogin") }}</button>
            <button
              type="submit"
              :disabled="submitDisabled"
              class="flex-1 h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
            >
              <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
              {{ submitLabel }}
            </button>
          </div>
        </form>

        <template v-if="showOAuthBlock">
          <div class="flex items-center gap-2 my-3">
            <div class="h-px flex-1 bg-[hsl(var(--border))]" />
            <span class="text-[11px] text-ink2">{{ $t("login.orDivider") }}</span>
            <div class="h-px flex-1 bg-[hsl(var(--border))]" />
          </div>
          <div class="flex items-center justify-center gap-3">
            <button
              v-if="providers.googleEnabled"
              type="button"
              @click="onOAuthClick('google')"
              :aria-label="$t('login.google')"
              class="w-11 h-11 rounded-full border border-border flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
            >
              <svg viewBox="0 0 18 18" class="w-[18px] h-[18px]" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
              </svg>
            </button>
            <button
              v-if="providers.telegramOidcEnabled"
              type="button"
              @click="onOAuthClick('telegram')"
              :aria-label="$t('login.telegramOidc')"
              class="w-11 h-11 rounded-full border border-border flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
            >
              <Send class="w-[18px] h-[18px]" />
            </button>
          </div>
        </template>

        <p v-if="mode === 'login' && providers.emailRegistrationEnabled" class="text-center text-[12px] mt-3">
          <button type="button" @click="switchMode('register')" class="font-semibold text-[hsl(var(--primary))] hover:underline">
            {{ $t("login.registerToggle") }}
          </button>
        </p>
        <p v-else-if="mode === 'register'" class="text-center text-[12px] mt-3">
          <button type="button" @click="switchMode('login')" class="font-semibold text-[hsl(var(--primary))] hover:underline">
            {{ $t("login.loginToggle") }}
          </button>
        </p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Mail, Lock, Phone, CircleAlert, CircleCheck, Loader2, Send, X } from "lucide-vue-next";
import { MorphIcon } from "morphicons/vue";
import { Eye as MorphEye, EyeOff as MorphEyeOff } from "lucide";
import logoMark from "../assets/logo-mark.png";
import api from "../api";
import { useAuthStore } from "../store/auth";
import { useAuthProvidersStore } from "../store/authProviders";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import CredentialConflictCard from "./CredentialConflictCard.vue";
import { startOAuthLogin, type OAuthProvider } from "../composables/useOAuthLogin";
import { useHistoryBackClose } from "../composables/useHistoryBackClose";

const { t } = useI18n();

const emit = defineEmits(["close", "logged"]);

// Кнопка "назад" в браузере закрывает модалку, а не листает страницу под ней.
useHistoryBackClose(() => emit("close"));

type Mode = "login" | "register" | "forgot" | "registered" | "forgotSent" | "conflict";
const mode = ref<Mode>("login");
const identifier = ref("");
const password = ref("");
const confirmPassword = ref("");
const showPassword = ref(false);
const loading = ref(false);
const error = ref("");
const conflict = ref<{
  profile: { username: string; display_name: string | null; avatar_url: string | null; login_methods: string[] };
  link_token: string | null;
} | null>(null);

// Вход по телефону: пароль или одноразовый код через WhatsApp.
const loginMethod = ref<"password" | "otp">("password");
const loginOtpSent = ref(false);
// Регистрация по телефону: сначала подтверждаем номер кодом, потом создаём
// пароль (см. backend app/auth/service.py::register_with_phone).
const registerPhoneStep = ref<"start" | "otp" | "password">("start");
// 4 ячейки под 4-значный код (app/auth/service.py::_generate_otp_code);
// otpCode ниже склеивает их для отправки на бэкенд.
const otpDigits = ref<string[]>(["", "", "", ""]);
const otpCode = computed(() => otpDigits.value.join(""));
let otpCellRefs: (HTMLInputElement | null)[] = [];
const phoneToken = ref("");
const phoneOptionalEmail = ref("");

const auth = useAuthStore();
const providers = useAuthProvidersStore();

// Автофокус на поле логина намеренно убран - на мобильном он сразу
// поднимает клавиатуру, и при невысоком экране она перекрывает
// альтернативные способы входа (Telegram/Google) ниже поля.
onMounted(() => {
  providers.fetch();
});

// "@" однозначно отличает email от телефона в любом формате (тот же
// признак, что использует backend в authenticate_password) - пустая строка
// намеренно не считается похожей на телефон, чтобы иконка/переключатели не
// появлялись раньше, чем пользователь начал печатать.
const looksLikePhone = computed(() => {
  const v = identifier.value.trim();
  return v.length > 0 && !v.includes("@");
});

// Email вход не поддерживает OTP - переключатель актуален только для
// телефона, форсируем обратно на пароль, если поле стало похоже на email.
watch(looksLikePhone, (isPhone) => {
  if (!isPhone) {
    loginMethod.value = "password";
    loginOtpSent.value = false;
  }
});

const setOtpCellRef = (el: HTMLInputElement | null, i: number) => {
  otpCellRefs[i] = el;
};

const focusOtpCell = (i: number) => {
  requestAnimationFrame(() => otpCellRefs[i]?.focus());
};

// Раскладывает вставленный/подставленный из буфера код по всем 4 ячейкам
// разом - используется и при явной вставке (onOtpPaste), и при попытке
// подставить код из буфера обмена автоматически (см. watch(showOtpField)).
const fillOtpDigits = (code: string) => {
  const digits = code.replace(/\D/g, "").slice(0, 4).split("");
  otpDigits.value = [...digits, ...Array(4 - digits.length).fill("")];
  focusOtpCell(digits.length < 4 ? digits.length : 3);
};

const onOtpDigitInput = (i: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  const digitsOnly = target.value.replace(/\D/g, "");
  // Некоторые мобильные клавиатуры (автоподстановка кода из QuickType и
  // т.п.) вставляют сразу несколько цифр через input, а не через paste.
  if (digitsOnly.length > 1) {
    fillOtpDigits(digitsOnly);
    return;
  }
  otpDigits.value[i] = digitsOnly;
  if (digitsOnly && i < 3) focusOtpCell(i + 1);
};

const onOtpDigitKeydown = (i: number, event: KeyboardEvent) => {
  if (event.key === "Backspace" && !otpDigits.value[i] && i > 0) {
    focusOtpCell(i - 1);
  }
};

const onOtpPaste = (i: number, event: ClipboardEvent) => {
  const text = event.clipboardData?.getData("text") ?? "";
  const digitsOnly = text.replace(/\D/g, "");
  if (digitsOnly.length >= 4) {
    event.preventDefault();
    fillOtpDigits(digitsOnly);
  }
};

const resetPhoneFlow = () => {
  registerPhoneStep.value = "start";
  loginOtpSent.value = false;
  otpDigits.value = ["", "", "", ""];
  phoneToken.value = "";
  error.value = "";
};

const resetFlowState = () => {
  password.value = "";
  confirmPassword.value = "";
  phoneOptionalEmail.value = "";
  loginMethod.value = "password";
  resetPhoneFlow();
};

const switchMode = (next: Mode) => {
  resetFlowState();
  mode.value = next;
};

// Вход по паролю (email или телефон) работает независимо от WhatsApp -
// authenticate_password принимает оба идентификатора всегда. OTP - это
// ДОПОЛНИТЕЛЬНЫЙ способ, доступный только если бэкенд реально может его
// отправить (BACKEND_WHATSAPP_*, см. GET /auth/providers), поэтому вся
// register-по-телефону-через-OTP ветка и OTP-логин гейтятся отдельно.
const phoneOtpAvailable = computed(() => looksLikePhone.value && providers.whatsappEnabled);

const identifierLocked = computed(
  () =>
    (mode.value === "register" && phoneOtpAvailable.value && registerPhoneStep.value !== "start") ||
    (mode.value === "login" && loginMethod.value === "otp" && loginOtpSent.value)
);

const showPasswordField = computed(() => {
  if (mode.value === "login") return loginMethod.value === "password";
  if (mode.value === "register") {
    if (!phoneOtpAvailable.value) return true;
    return registerPhoneStep.value === "password";
  }
  return false;
});

const showPasswordConfirm = computed(
  () => mode.value === "register" && (!phoneOtpAvailable.value || registerPhoneStep.value === "password")
);

const showOptionalEmail = computed(
  () => mode.value === "register" && phoneOtpAvailable.value && registerPhoneStep.value === "password"
);

const showOtpField = computed(
  () =>
    (mode.value === "login" && loginMethod.value === "otp" && loginOtpSent.value) ||
    (mode.value === "register" && phoneOtpAvailable.value && registerPhoneStep.value === "otp")
);

const showLoginMethodToggle = computed(
  () => mode.value === "login" && phoneOtpAvailable.value && !loginOtpSent.value
);

// Best-effort автоподстановка из буфера обмена - readText() может быть
// недоступен/отклонён, тогда просто фокусируем первую ячейку.
watch(showOtpField, async (shown) => {
  if (!shown) return;
  focusOtpCell(0);
  try {
    const text = await navigator.clipboard?.readText();
    const digitsOnly = (text ?? "").replace(/\D/g, "");
    if (digitsOnly.length === 4) {
      fillOtpDigits(digitsOnly);
    }
  } catch {
    // разрешение не дано/API недоступен - тихо игнорируем
  }
});

const showOAuthBlock = computed(
  () =>
    (providers.googleEnabled || providers.telegramOidcEnabled) &&
    ((mode.value === "login" && loginMethod.value === "password") ||
      (mode.value === "register" && !(phoneOtpAvailable.value && registerPhoneStep.value !== "start")))
);

const title = computed(() => {
  if (mode.value === "register") {
    if (phoneOtpAvailable.value && registerPhoneStep.value === "otp") return t("login.phoneOtpStepTitle");
    if (phoneOtpAvailable.value && registerPhoneStep.value === "password") return t("login.phonePasswordStepTitle");
    return t("login.registerTitle");
  }
  if (mode.value === "forgot") return t("login.forgotTitle");
  return t("login.title");
});

const subtitle = computed(() => {
  if (mode.value === "register" && phoneOtpAvailable.value && registerPhoneStep.value === "otp") {
    return t("login.phoneOtpStepSubtitle", { phone: identifier.value });
  }
  if (mode.value === "register" && phoneOtpAvailable.value && registerPhoneStep.value === "password") {
    return t("login.phonePasswordStepSubtitle");
  }
  if (mode.value === "login" && loginMethod.value === "otp" && loginOtpSent.value) {
    return t("login.phoneOtpStepSubtitle", { phone: identifier.value });
  }
  if (mode.value === "forgot") return t("login.forgotSubtitle");
  return t("login.subtitle");
});

const submitLabel = computed(() => {
  if (mode.value === "login") {
    if (loginMethod.value === "otp") {
      if (!loginOtpSent.value) return loading.value ? t("login.sendCodeLoading") : t("login.sendCode");
      return loading.value ? t("login.submitLoading") : t("login.submit");
    }
    return loading.value ? t("login.submitLoading") : t("login.submit");
  }
  if (mode.value === "register") {
    if (phoneOtpAvailable.value) {
      if (registerPhoneStep.value === "start") return loading.value ? t("login.sendCodeLoading") : t("login.sendCode");
      if (registerPhoneStep.value === "otp") return loading.value ? t("login.verifyCodeLoading") : t("login.verifyCode");
    }
    return loading.value ? t("login.registerSubmitLoading") : t("login.registerSubmit");
  }
  return loading.value ? t("login.forgotSubmitLoading") : t("login.forgotSubmit");
});

const submitDisabled = computed(() => {
  if (loading.value) return true;
  if (mode.value === "forgot") return !identifier.value;
  if (mode.value === "login") {
    if (loginMethod.value === "otp") return loginOtpSent.value ? !otpCode.value : !identifier.value;
    return !identifier.value || !password.value;
  }
  if (mode.value === "register") {
    if (phoneOtpAvailable.value) {
      if (registerPhoneStep.value === "start") return !identifier.value;
      if (registerPhoneStep.value === "otp") return !otpCode.value;
      return !password.value || !confirmPassword.value;
    }
    return !identifier.value || !password.value || !confirmPassword.value;
  }
  return true;
});

const toggleLoginMethod = () => {
  loginMethod.value = loginMethod.value === "password" ? "otp" : "password";
  loginOtpSent.value = false;
  otpDigits.value = ["", "", "", ""];
  error.value = "";
};

// Общий финал для всех способов входа, доводящих до токенов сразу (email
// login, phone OTP login, phone-регистрация, OAuth без коллизии) -
// подтягивает профиль, чтобы стор был в синхроне.
const finishLogin = async (tokens: { access_token: string; refresh_token: string }) => {
  auth.setTokens(tokens.access_token, tokens.refresh_token);
  const me = await api.get("/auth/me");
  auth.setProfile(me.data);
  emit("logged");
  emit("close");
};

// Общая обработка 409 credential_taken для обоих OTP-эндпоинтов (send/verify) -
// тот же контракт "это вы?", что уже обслуживает email/Google/Telegram
// коллизии (см. CredentialConflictCard - его форма пароля теперь принимает
// и телефон, не только email).
const handleCredentialTaken = (err: any): boolean => {
  const detail = err?.response?.data?.detail;
  if (err?.response?.status === 409 && detail?.error === "credential_taken") {
    conflict.value = { profile: detail.profile, link_token: detail.link_token };
    mode.value = "conflict";
    return true;
  }
  return false;
};

const handleOtpError = (err: any, fallback: string) => {
  if (handleCredentialTaken(err)) return;
  const detail = err?.response?.data?.detail;
  if (err?.response?.status === 400 && detail === "invalid_phone") {
    error.value = t("login.invalidPhone");
  } else if (err?.response?.status === 429) {
    error.value = t("login.otpRateLimited");
  } else if (err?.response?.status === 400) {
    error.value = t("login.otpInvalid");
  } else {
    error.value = fallback;
  }
};

const submitLogin = async () => {
  loading.value = true;
  error.value = "";
  try {
    const form = new URLSearchParams();
    form.append("username", identifier.value);
    form.append("password", password.value);
    // OAuth2PasswordRequestForm (pydantic 2.9) требует grant_type строго "password" - пустая строка даёт 422.
    form.append("grant_type", "password");
    form.append("scope", "");
    const res = await api.post("/auth/login", form, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    await finishLogin(res.data);
  } catch (err: any) {
    if (err?.response?.status === 403 && err?.response?.data?.detail === "email_not_verified") {
      error.value = t("login.emailNotVerifiedError");
      // Логин с неподтверждённой почтой - подходящий момент, чтобы письмо
      // ушло заново (пользователь мог случайно закрыть его/не получить).
      api.post("/auth/resend-verification", { email: identifier.value }).catch(() => {});
    } else {
      error.value = err?.response?.status === 401 ? t("login.errorInvalid") : t("login.errorGeneric");
    }
  } finally {
    loading.value = false;
  }
};

const sendLoginOtp = async () => {
  loading.value = true;
  error.value = "";
  try {
    await api.post("/auth/phone/otp/send", { phone: identifier.value, purpose: "login" });
    loginOtpSent.value = true;
  } catch (err: any) {
    handleOtpError(err, t("login.errorGeneric"));
  } finally {
    loading.value = false;
  }
};

const verifyLoginOtp = async () => {
  loading.value = true;
  error.value = "";
  try {
    const res = await api.post("/auth/phone/otp/verify", {
      phone: identifier.value,
      code: otpCode.value,
      purpose: "login",
    });
    await finishLogin(res.data.tokens);
  } catch (err: any) {
    handleOtpError(err, t("login.errorGeneric"));
  } finally {
    loading.value = false;
  }
};

const submitRegister = async () => {
  if (password.value !== confirmPassword.value) {
    error.value = t("login.passwordMismatch");
    return;
  }
  loading.value = true;
  error.value = "";
  try {
    await api.post("/auth/register", { email: identifier.value, password: password.value });
    mode.value = "registered";
  } catch (err: any) {
    if (!handleCredentialTaken(err)) {
      error.value = t("login.registerErrorGeneric");
    }
  } finally {
    loading.value = false;
  }
};

const sendRegisterOtp = async () => {
  loading.value = true;
  error.value = "";
  try {
    await api.post("/auth/phone/otp/send", { phone: identifier.value, purpose: "register" });
    registerPhoneStep.value = "otp";
  } catch (err: any) {
    handleOtpError(err, t("login.registerErrorGeneric"));
  } finally {
    loading.value = false;
  }
};

const verifyRegisterOtp = async () => {
  loading.value = true;
  error.value = "";
  try {
    const res = await api.post("/auth/phone/otp/verify", {
      phone: identifier.value,
      code: otpCode.value,
      purpose: "register",
    });
    phoneToken.value = res.data.phone_token;
    registerPhoneStep.value = "password";
  } catch (err: any) {
    handleOtpError(err, t("login.registerErrorGeneric"));
  } finally {
    loading.value = false;
  }
};

const submitPhoneRegister = async () => {
  if (password.value !== confirmPassword.value) {
    error.value = t("login.passwordMismatch");
    return;
  }
  loading.value = true;
  error.value = "";
  try {
    const res = await api.post("/auth/phone/register", {
      phone_token: phoneToken.value,
      password: password.value,
      email: phoneOptionalEmail.value || undefined,
    });
    await finishLogin(res.data);
  } catch (err: any) {
    if (!handleCredentialTaken(err)) {
      error.value = t("login.registerErrorGeneric");
    }
  } finally {
    loading.value = false;
  }
};

const submitForgot = async () => {
  loading.value = true;
  error.value = "";
  try {
    await api.post("/auth/forgot-password", { email: identifier.value });
    mode.value = "forgotSent";
  } catch {
    error.value = t("login.errorGeneric");
  } finally {
    loading.value = false;
  }
};

const onSubmit = () => {
  if (mode.value === "login") {
    if (loginMethod.value === "otp") {
      return loginOtpSent.value ? verifyLoginOtp() : sendLoginOtp();
    }
    return submitLogin();
  }
  if (mode.value === "register") {
    if (phoneOtpAvailable.value) {
      if (registerPhoneStep.value === "start") return sendRegisterOtp();
      if (registerPhoneStep.value === "otp") return verifyRegisterOtp();
      return submitPhoneRegister();
    }
    return submitRegister();
  }
  return submitForgot();
};

const onCancelClick = () => {
  if (mode.value === "login") {
    emit("close");
  } else {
    switchMode("login");
  }
};

// Коллизия сюда попадает только из register-флоу (email/OTP по телефону) -
// "отмена" возвращает к форме регистрации, не к логину.
const onCancelConflict = () => {
  mode.value = "register";
};

const onOAuthClick = async (provider: OAuthProvider) => {
  error.value = "";
  try {
    await startOAuthLogin(provider, "login");
  } catch {
    error.value = t("login.oauthGenericError");
  }
};

// Коллизия при регистрации разрешена (пользователь вошёл в СУЩЕСТВУЮЩИЙ
// профиль и credential довязан) - CredentialConflictCard уже сам обновил
// auth.setProfile, здесь только закрыть модалку тем же путём, что и обычный логин.
const onConflictResolved = () => {
  emit("logged");
  emit("close");
};

// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, { onClose: () => emit('close') });
</script>
